import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { 
  Building2, 
  Calendar, 
  Clock, 
  ArrowLeft,
  HardHat,
  Ruler,
  Wrench,
  CheckCircle,
  AlertTriangle,
  FileText,
  Target,
  TrendingUp,
  Shield,
  Leaf,
  Euro,
  Users
} from 'lucide-react';

export default function MarchesPublicsBatimentArticle() {
  return (
    <>
      <SEO 
        title="Marchés Publics BTP et Bâtiment : Guide Complet 2026"
        description="Comment répondre aux appels d'offres de travaux publics ? Qualifications, garanties, mémoire technique BTP. Guide complet pour les entreprises du bâtiment."
        path="/blog/marches-publics-batiment-btp"
      />
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-dce-express.png" alt="DCE Express" className="h-10 w-auto" />
            </Link>
            <div className="flex gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Article */}
        <article className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Breadcrumb */}
            <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                  BTP
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                  Guide Complet
                </span>
              </div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                Marchés Publics de Travaux : Guide Complet pour les Entreprises du BTP
              </h1>
              <div className="flex items-center gap-6 text-slate-500">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  30 mars 2026
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  15 min de lecture
                </span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="relative h-80 rounded-2xl overflow-hidden mb-10">
              <img 
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200" 
                alt="Chantier de construction BTP"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-lg font-medium">
                  Les marchés publics de travaux représentent plus de 40 milliards d'euros par an en France
                </p>
              </div>
            </div>

            {/* Table of Contents */}
            <div className="bg-slate-50 rounded-xl p-6 mb-10">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-600" />
                Sommaire
              </h2>
              <ul className="space-y-2 text-slate-600">
                <li><a href="#introduction" className="hover:text-violet-600">1. Introduction aux marchés de travaux</a></li>
                <li><a href="#types" className="hover:text-violet-600">2. Types de marchés de travaux</a></li>
                <li><a href="#qualifications" className="hover:text-violet-600">3. Qualifications et certifications requises</a></li>
                <li><a href="#dce" className="hover:text-violet-600">4. Comprendre le DCE travaux</a></li>
                <li><a href="#memoire" className="hover:text-violet-600">5. Rédiger un mémoire technique BTP</a></li>
                <li><a href="#prix" className="hover:text-violet-600">6. Établir son offre de prix</a></li>
                <li><a href="#garanties" className="hover:text-violet-600">7. Garanties et assurances</a></li>
                <li><a href="#execution" className="hover:text-violet-600">8. Exécution du marché</a></li>
              </ul>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <section id="introduction" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <HardHat className="w-6 h-6 text-orange-600" />
                  1. Introduction aux Marchés de Travaux
                </h2>
                <p className="text-slate-600 mb-4">
                  Les <strong>marchés publics de travaux</strong> concernent la réalisation de tous travaux de bâtiment ou de génie civil : construction, rénovation, démolition, aménagement, voirie, réseaux... Ils sont régis par le Code de la commande publique et représentent une part majeure de l'activité du secteur BTP.
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 my-6">
                  <div className="bg-orange-50 rounded-xl p-5 text-center">
                    <div className="text-3xl font-bold text-orange-600 mb-2">40 Mds€</div>
                    <p className="text-slate-600 text-sm">Volume annuel des marchés de travaux publics</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-5 text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">150 000+</div>
                    <p className="text-slate-600 text-sm">Appels d'offres travaux par an</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-5 text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">70%</div>
                    <p className="text-slate-600 text-sm">Attribués à des PME du BTP</p>
                  </div>
                </div>

                <div className="bg-violet-50 border-l-4 border-violet-500 p-6 rounded-r-lg">
                  <p className="text-violet-900">
                    <strong>Bon à savoir :</strong> Les collectivités territoriales (communes, départements, régions) sont les principaux donneurs d'ordre, suivies par l'État et les établissements publics.
                  </p>
                </div>
              </section>

              {/* Types de marchés */}
              <section id="types" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-blue-600" />
                  2. Types de Marchés de Travaux
                </h2>

                <div className="space-y-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                      <Euro className="w-5 h-5 text-green-600" />
                      Marchés à procédure adaptée (MAPA) - Moins de 5 382 000 € HT
                    </h4>
                    <p className="text-slate-600 text-sm">
                      Procédure simplifiée, délais courts, modalités libres. Idéal pour les PME. Représente la majorité des marchés de travaux.
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-600" />
                      Appel d'offres ouvert - Plus de 5 382 000 € HT
                    </h4>
                    <p className="text-slate-600 text-sm">
                      Procédure formalisée, publication au JOUE, délais minimaux de 30 jours. Critères d'attribution définis dans le règlement de consultation.
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      Appel d'offres restreint
                    </h4>
                    <p className="text-slate-600 text-sm">
                      Deux phases : candidature puis offre. Permet de présélectionner les entreprises qualifiées avant l'envoi du DCE complet.
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-orange-600" />
                      Marché de conception-réalisation
                    </h4>
                    <p className="text-slate-600 text-sm">
                      L'entreprise ou le groupement assure à la fois les études (conception) et les travaux (réalisation). Nécessite des compétences élargies.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mt-6">
                  <p className="text-amber-900">
                    <strong>Allotissement obligatoire :</strong> Depuis 2016, les marchés doivent être découpés en lots séparés pour favoriser l'accès des PME. L'acheteur doit justifier s'il ne le fait pas.
                  </p>
                </div>
              </section>

              {/* Qualifications */}
              <section id="qualifications" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Shield className="w-6 h-6 text-green-600" />
                  3. Qualifications et Certifications Requises
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Pour répondre aux marchés de travaux, les entreprises doivent justifier de capacités techniques et professionnelles adaptées.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-green-50 rounded-xl p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Qualifications Qualibat</h4>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Gros œuvre, maçonnerie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Électricité, plomberie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Menuiserie, serrurerie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>Peinture, revêtements</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        <span>CVC, chauffage</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Certifications spécifiques</h4>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>RGE</strong> - Travaux énergétiques</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>FNTP</strong> - Travaux publics</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>Qualifelec</strong> - Électricité</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>Qualigaz</strong> - Installations gaz</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>OPQIBI</strong> - Ingénierie</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-bold text-slate-900 mb-3">Documents à fournir (candidature)</h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
                    <ul className="space-y-1">
                      <li>• DC1 / DC2 (formulaires candidature)</li>
                      <li>• Attestations fiscales et sociales</li>
                      <li>• Extrait Kbis de moins de 3 mois</li>
                      <li>• Attestation d'assurance décennale</li>
                    </ul>
                    <ul className="space-y-1">
                      <li>• Certificats de qualification (Qualibat...)</li>
                      <li>• Références de chantiers similaires</li>
                      <li>• Chiffre d'affaires des 3 dernières années</li>
                      <li>• Effectifs et moyens techniques</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Comprendre le DCE */}
              <section id="dce" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-slate-600" />
                  4. Comprendre le DCE Travaux
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Le Dossier de Consultation des Entreprises (DCE) pour les marchés de travaux comprend généralement :
                </p>

                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-violet-600">RC</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Règlement de Consultation</h4>
                      <p className="text-slate-600 text-sm">Règles de la consultation, critères d'attribution et leur pondération, délais, modalités de remise des offres.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-blue-600">CCTP</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Cahier des Clauses Techniques Particulières</h4>
                      <p className="text-slate-600 text-sm">Description détaillée des travaux, spécifications techniques, matériaux, normes à respecter.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-green-600">CCAP</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Cahier des Clauses Administratives Particulières</h4>
                      <p className="text-slate-600 text-sm">Conditions contractuelles : délais, pénalités, modalités de paiement, assurances, sous-traitance.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-orange-600">Plans</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Plans et Documents Graphiques</h4>
                      <p className="text-slate-600 text-sm">Plans architecturaux, plans techniques, coupes, détails d'exécution, études géotechniques.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-amber-600">BPU</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Bordereau des Prix Unitaires / DQE</h4>
                      <p className="text-slate-600 text-sm">Liste des ouvrages à chiffrer avec quantités estimatives. Base de l'offre financière.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Mémoire technique */}
              <section id="memoire" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Ruler className="w-6 h-6 text-violet-600" />
                  5. Rédiger un Mémoire Technique BTP Gagnant
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Le mémoire technique est souvent le document le plus important de votre offre. Il représente généralement 40 à 60% de la note finale.
                </p>

                <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-6 mb-6">
                  <h4 className="font-bold text-slate-900 mb-4">Structure type d'un mémoire technique BTP</h4>
                  <ol className="space-y-3 text-slate-700">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                      <span><strong>Présentation de l'entreprise</strong> : historique, organisation, moyens humains et matériels</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                      <span><strong>Compréhension du projet</strong> : analyse du DCE, contraintes identifiées, points de vigilance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                      <span><strong>Méthodologie d'exécution</strong> : phasage, organisation du chantier, méthodes constructives</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                      <span><strong>Planning détaillé</strong> : diagramme de Gantt, jalons, chemins critiques</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">5</span>
                      <span><strong>Équipe dédiée</strong> : organigramme, CV du conducteur de travaux, chef de chantier</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">6</span>
                      <span><strong>Sécurité et environnement</strong> : PPSPS, gestion des déchets, nuisances chantier</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm">7</span>
                      <span><strong>Références similaires</strong> : chantiers comparables réalisés avec photos et attestations</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                  <p className="text-green-900">
                    <strong>Conseil :</strong> Personnalisez chaque mémoire technique au projet. Les copier-coller génériques sont immédiatement repérés et mal notés par les acheteurs.
                  </p>
                </div>
              </section>

              {/* Offre de prix */}
              <section id="prix" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Euro className="w-6 h-6 text-green-600" />
                  6. Établir Son Offre de Prix
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Analyser le BPU/DQE</h4>
                    <p className="text-slate-600 text-sm">
                      Vérifiez les quantités, identifiez les incohérences, posez des questions à l'acheteur si nécessaire. Une erreur de métré peut être fatale.
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Décomposer les prix</h4>
                    <p className="text-slate-600 text-sm">
                      Main d'œuvre + matériaux + matériel + frais de chantier + frais généraux + marge. Justifiez vos prix si demandé.
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Attention aux prix anormalement bas</h4>
                    <p className="text-slate-600 text-sm">
                      L'acheteur peut écarter une offre dont le prix est anormalement bas. Vous devrez justifier votre compétitivité.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                  <p className="text-amber-900">
                    <strong>Rappel :</strong> Le prix n'est pas tout. Un marché attribué au "mieux-disant" (et non au moins-disant) valorise aussi la qualité technique, les délais et les engagements RSE.
                  </p>
                </div>
              </section>

              {/* Garanties */}
              <section id="garanties" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Shield className="w-6 h-6 text-blue-600" />
                  7. Garanties et Assurances Obligatoires
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 rounded-xl p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Assurances requises</h4>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>Décennale</strong> : couvre les dommages compromettant la solidité de l'ouvrage pendant 10 ans</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>RC Professionnelle</strong> : dommages causés aux tiers pendant les travaux</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                        <span><strong>Tous Risques Chantier</strong> : dommages à l'ouvrage en cours de construction</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-orange-50 rounded-xl p-6">
                    <h4 className="font-bold text-slate-900 mb-3">Garanties contractuelles</h4>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                        <span><strong>Retenue de garantie</strong> : 5% du montant du marché, libérée après 1 an</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                        <span><strong>Garantie de parfait achèvement</strong> : 1 an après réception</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                        <span><strong>Caution bancaire</strong> : peut remplacer la retenue de garantie</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Exécution */}
              <section id="execution" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Wrench className="w-6 h-6 text-orange-600" />
                  8. Exécution du Marché
                </h2>
                
                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Ordre de service</h4>
                      <p className="text-slate-600 text-sm">Le marché démarre à réception de l'OS. Vérifiez les délais et préparez l'installation de chantier.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Suivi de chantier</h4>
                      <p className="text-slate-600 text-sm">Réunions de chantier hebdomadaires, comptes-rendus, gestion des aléas et travaux modificatifs.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-violet-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Situations de travaux</h4>
                      <p className="text-slate-600 text-sm">Facturation mensuelle selon l'avancement. Délai de paiement de 30 jours maximum.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">Réception des travaux</h4>
                      <p className="text-slate-600 text-sm">OPR, levée des réserves, PV de réception. Déclenche les délais de garantie.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* CTA */}
              <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl p-8 text-center text-white">
                <h3 className="text-2xl font-bold mb-4">
                  Trouvez des marchés de travaux adaptés à votre entreprise
                </h3>
                <p className="text-orange-100 mb-6 max-w-xl mx-auto">
                  DCE Express analyse quotidiennement les appels d'offres BTP et vous recommande les plus pertinents selon vos qualifications.
                </p>
                <Link to="/register">
                  <Button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-3 text-lg">
                    Essayer gratuitement
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <p className="text-slate-400">
              © 2026 DCE Express - Tous droits réservés
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
